import { graphql } from "babel-plugin-relay/macro";
import { PreloadedQuery, usePreloadedQuery, useQueryLoader } from "react-relay";
import APIOverviewCustomerQueryGraphql, {
    APIOverviewCustomerQuery,
} from "./__generated__/APIOverviewCustomerQuery.graphql";
import { Link, useParams } from "react-router-dom";
import { BadRequestErrorPage, RouteNotFoundErrorPage } from "./ErrorPage";
import { useTitle } from "../lib/title";
import { withDateRange } from "../lib/urlParams";
import { DateRangeDisplay } from "../components/DateRange";
import { SymmetricBar } from "../components/utils";
import React, { useCallback, useState } from "react";
import { DateRange } from "../lib/dateUtils";
import { format } from "date-fns";
import { useDataReloadOnDateChange } from "./APIOverview";
import parseInt from "lodash/parseInt";
import { Breadcrumb } from "react-bootstrap";
import { Toggle } from "../components/Toggles";
import styles from "./APIOverview.module.scss";


function _APIOverviewCustomer() {
    useTitle("API Overview");

    const params = useParams();

    const [customerDataQueryRef, loadCustomerDataQuery] =
        useQueryLoader<APIOverviewCustomerQuery>(
            APIOverviewCustomerQueryGraphql
        );

    const [showUsers, setShowUsers] = useState(false);

    const loadCustomerData = useCallback((dateRange: DateRange) => {
        loadCustomerDataQuery({
            customerID: parseInt(params.customerID as string),
            start: format(dateRange.start, "yyyy-MM-dd"),
            end: format(dateRange.end, "yyyy-MM-dd"),
            groupByUser: showUsers
        });
    }, [loadCustomerDataQuery, params.customerID, showUsers]);

    useDataReloadOnDateChange(loadCustomerData);

    if (customerDataQueryRef && !customerDataQueryRef.variables.customerID) {
        return <BadRequestErrorPage data-testid="bad-request-error-page" />;
    }

    return (
        <>
            {customerDataQueryRef && (
                <APIOverviewCustomerData queryRef={customerDataQueryRef} showUsers={showUsers} onShowUsersChange={setShowUsers}/>
            )}
        </>
    );
}

export const APIOverviewCustomer = withDateRange(_APIOverviewCustomer, 7);

function APIOverviewCustomerData(props: {
    queryRef: PreloadedQuery<APIOverviewCustomerQuery>;
    showUsers: boolean;
    onShowUsersChange: (showUsers: boolean) => void;
}) {
    const response = usePreloadedQuery<APIOverviewCustomerQuery>(
        graphql`
        query APIOverviewCustomerQuery(
            $customerID: Int!
            $start: Date!
            $end: Date!
            $groupByUser: Boolean!
        ) {
            customer(customerID: $customerID) {
                name
                apiStats(dateRange: { start: $start, end: $end }, groupByUser: $groupByUser) {
                    path
                    queryString
                    method
                    login
                    totalCount
                }
            }
        }
    `, props.queryRef
    );

   if (!response.customer) {
       return <RouteNotFoundErrorPage data-testid="not-found-error-page" />;
   }

    return (
        <div className={styles.container}>
            <Breadcrumb className={styles.breadcrumb}>
                <Breadcrumb.Item
                    linkAs={Link}
                    linkProps={{
                        to: "/admin/APIOverview" + window.location.search,
                    }}
                >
                    API Overview
                </Breadcrumb.Item>
                <Breadcrumb.Item active>
                    {response.customer.name}
                </Breadcrumb.Item>
            </Breadcrumb>
            <div className={styles.header}>
                <h1>{response.customer.name}</h1>
            </div>
            <div className={styles.toggleSection}>
                <Toggle
                    enabled={props.showUsers}
                    onToggle={props.onShowUsersChange}
                    label={"Show API Usage by Users"}
                />
            </div>
            <SymmetricBar middle={<DateRangeDisplay />} />
            <table className="table table-hover">
                <thead>
                    <tr>
                        <th>Path</th>
                        <th>Method</th>
                        <th>Query String</th>
                        {props.showUsers && <th>User</th>}
                        <th>Total Count</th>
                    </tr>
                </thead>

                <tbody>
                    {response.customer.apiStats.map((apiStat) => {
                        const key = `${apiStat.path}-${apiStat.method}-${apiStat.queryString}-${apiStat.login}`;
                        return (
                            <tr key={key}>
                                <td>{apiStat.path}</td>
                                <td>{apiStat.method}</td>
                                <td>{apiStat.queryString}</td>
                                {props.showUsers && (
                                    <td>
                                        {props.showUsers
                                            ? apiStat.login
                                            : null}
                                    </td>
                                )}
                                <td>{apiStat.totalCount}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}
